import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import Swal from 'sweetalert2';

const CANONICAL = {
  incominginvoice: 'incomingInvoices',
  'incoming_invoices': 'incomingInvoices',
};
const normalizeModule = (m) => {
  if (!m) return m;
  const key = String(m).toLowerCase();
  return CANONICAL[key] || m;
};

const EMPTY_PERMS = {
  invoices: { view: false, create: false, update: false, delete: false },
  incomingInvoices: { view: false, create: false, update: false, delete: false }, // <-- kritikteki satır
  companies: { view: false, create: false, update: false, delete: false },
  customers: { view: false, create: false, update: false, delete: false },
  employees: { view: false, create: false, update: false, delete: false },
  users: { view: false, create: false, update: false, delete: false },
  settings: { view: false, create: false, update: false, delete: false },
  templates: { view: false, create: false, update: false, delete: false },
  standardDocuments: { view: false, create: false, update: false, delete: false },
  taxCalculation: { view: false, create: false, update: false, delete: false },
  smtpSettings: { view: false, create: false, update: false, delete: false },
  mailTemplates: { view: false, create: false, update: false, delete: false },
  permissionGroups: { view: false, create: false, update: false, delete: false },
  apiDocumentation: { view: false, create: false, update: false, delete: false },
};

const actions = ['view', 'create', 'update', 'delete'];

const PermissionGroups = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const modules = useMemo(() => Object.keys(EMPTY_PERMS), []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: { ...EMPTY_PERMS },
    isDefault: false,
  });

  // Fetch permission groups (her mount'ta tazele)
  const { data: permissionGroups = [], isLoading, error } = useQuery({
    queryKey: ['permission-groups'],
    queryFn: async () => {
      const res = await api.get('/permission-groups');
      return res.data || [];
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Create / Update
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      const permissions = [];
      for (const module of modules) {
        const entry = data.permissions?.[module];
        if (entry) {
          permissions.push({
            module, // canonical
            view: !!entry.view,
            create: !!entry.create,
            update: !!entry.update,
            delete: !!entry.delete,
          });
        }
      }

      const payload = {
        name: data.name?.trim(),
        description: data.description?.trim() || '',
        permissions,
        isDefault: !!data.isDefault,
      };

      if (editingGroup?._id) {
        const res = await api.put(`/permission-groups/${editingGroup._id}`, payload, {
          params: { runValidators: true },
        });
        return res.data;
      } else {
        const res = await api.post('/permission-groups', payload);
        return res.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-groups'] });
      setIsModalOpen(false);
      setEditingGroup(null);
      setIsSubmitting(false);
      resetForm();
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: editingGroup ? t('permissions.permissionGroupUpdatedSuccess') : t('permissions.permissionGroupCreatedSuccess'),
        timer: 1600,
        showConfirmButton: false,
      });
    },
    onError: (err) => {
      setIsSubmitting(false);
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: err?.response?.data?.message || t('permissions.permissionGroupSaveFailed'),
        confirmButtonText: t('common.ok'),
      });
    },
  });

  // Delete
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await api.delete(`/permission-groups/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permission-groups'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('permissions.permissionGroupDeletedSuccess'),
        timer: 1400,
        showConfirmButton: false,
      });
    },
    onError: (err) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: err?.response?.data?.message || t('permissions.permissionGroupDeleteFailed'),
        confirmButtonText: t('common.ok'),
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: { ...EMPTY_PERMS },
      isDefault: false,
    });
  };

  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroup(group);

      // API -> form: normalize et ve duplicate'leri ez
      const base = { ...EMPTY_PERMS };
      (group.permissions || []).forEach((perm) => {
        const key = normalizeModule(perm?.module);
        if (!key || !base[key]) return;
        base[key] = {
          view: !!perm.view,
          create: !!perm.create,
          update: !!perm.update,
          delete: !!perm.delete,
        };
      });

      setFormData({
        name: group.name || '',
        description: group.description || '',
        permissions: base,
        isDefault: !!group.isDefault,
      });
    } else {
      setEditingGroup(null);
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingGroup(null);
    setIsSubmitting(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePermissionChange = (module, action) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [action]: !prev.permissions[module][action],
        },
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (saveMutation.isPending || isSubmitting) return;

    if (!formData.name?.trim()) {
      Swal.fire({
        icon: 'warning',
        title: t('common.warning'),
        text: t('permissions.nameRequired') || 'Name is required',
      });
      return;
    }

    setIsSubmitting(true);
    saveMutation.mutate(formData);
  };

  const handleDelete = (id) => {
    const group = permissionGroups.find((g) => g._id === id);
    if (group?.isDefault) {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: t('permissions.cannotDeleteDefaultGroup'),
        confirmButtonText: t('common.ok'),
      });
      return;
    }

    Swal.fire({
      title: t('permissions.confirmDelete'),
      text: t('permissions.cannotRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('permissions.yesDelete'),
    }).then((res) => {
      if (res.isConfirmed) deleteMutation.mutate(id);
    });
  };

  // Liste rozetleri için normalize edilmiş görünüm
  const normalizedGroups = useMemo(() => {
    return (permissionGroups || []).map((g) => ({
      ...g,
      permissions: (g.permissions || []).map((p) => ({
        ...p,
        module: normalizeModule(p.module),
      })),
    }));
  }, [permissionGroups]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600 font-semibold">{t('common.error')}</div>
        <div className="text-sm text-gray-600">{String(error?.message || error)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('permissions.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('permissions.subtitle')}</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn btn-primary">
          {t('permissions.addPermissionGroup')}
        </button>
      </div>

      {/* Permission Groups List */}
      <div className="card">
        {normalizedGroups.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">🔐</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('permissions.noPermissionGroups')}</h3>
            <p className="text-gray-500 mb-4">{t('permissions.createFirstPermissionGroup')}</p>
            <button onClick={() => handleOpenModal()} className="btn btn-primary">
              {t('permissions.addPermissionGroup')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('permissions.name')}</th>
                  <th>{t('permissions.description')}</th>
                  <th>{t('permissions.default')}</th>
                  <th>{t('permissions.modules')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {normalizedGroups.map((group) => (
                  <tr key={group._id}>
                    <td className="font-medium">{group.name}</td>
                    <td>{group.description}</td>
                    <td>
                      {group.isDefault ? (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {t('permissions.default')}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {modules.map((module) => {
                          const modulePerm = group.permissions?.find((p) => p.module === module);
                          if (
                            modulePerm &&
                            (modulePerm.view || modulePerm.create || modulePerm.update || modulePerm.delete)
                          ) {
                            return (
                              <span key={module} className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                {t(`permissions.modules.${module}`)}
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleOpenModal(group)}
                          className="text-blue-600 hover:text-blue-500 text-sm"
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(group._id)}
                          disabled={group.isDefault}
                          className={`text-sm ${
                            group.isDefault ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-500'
                          }`}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {editingGroup ? t('permissions.editPermissionGroup') : t('permissions.addPermissionGroup')}
              </h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('permissions.name')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('permissions.description')}
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{t('permissions.setAsDefault')}</span>
                </label>
              </div>

              {/* Permission Matrix */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">{t('permissions.permissions')}</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          {t('permissions.module')}
                        </th>
                        {actions.map((action) => (
                          <th key={action} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                            {t(`permissions.actions.${action}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {modules.map((module) => (
                        <tr key={module}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {t(`permissions.modules.${module}`)}
                          </td>
                          {actions.map((action) => (
                            <td key={action} className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={formData.permissions[module]?.[action] || false}
                                onChange={() => handlePermissionChange(module, action)}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending || isSubmitting}
                  className="btn btn-primary"
                >
                  {saveMutation.isPending || isSubmitting
                    ? t('common.saving')
                    : editingGroup
                    ? t('common.update')
                    : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionGroups;
